import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DataService } from '../data/data.service';
import { CartItem } from '../data/models';
import { ProductDetailsService } from '../products/product-details.service';

@Component({
    selector: 'app-cart-item-edit',
    template: `
    <form [formGroup]="form" (submit)="updateProduct()">
      <div class="modal-header p-3">
        <h4 class="modal-title" i18n>Artikel bearbeiten</h4>
        <button type="button" class="btn-close" i18n-aria-label aria-label="Close"
                (click)="activeModal.dismiss('Cross click')"></button>
      </div>
      <div class="modal-body p-3">
        <div class="d-flex w-100 flex-column">
          <div class="d-flex justify-content-between justify-content-md-start py-3 border-bottom">
            <div class="me-5 d-none d-md-block">
              <div
                class="d-md-block w-80px h-80px product-image-placeholder bg-white shadow-sm border"
                [style.background-image]="image && 'url(' + image + ')'">
                <div class="w-100 h-100 position-relative bg-transparent">
                  <button type="button"
                          (click)="onProductInfoClicked()"
                          class="btn btn-outline-secondary rounded-circle border-0 shadow product-info-button bg-white bg-hover-info">
                    <span class="material-icons fs-5">info</span>
                  </button>
                </div>
              </div>
            </div>
            <div class="d-flex flex-column">
              <span class="fw-bolder">{{lineItem.title}}</span>
              <span class="text-info fs-5">{{lineItem.price | currency}}</span>
            </div>
            <div class="d-flex d-md-none justify-content-center align-items-center">
              <button type="button"
                      (click)="onProductInfoClicked()"
                      class="btn btn-outline-secondary rounded-circle border-0 shadow bg-white bg-hover-info">
                <span class="material-icons fs-5">info</span>
              </button>
            </div>
          </div>
          <div class="d-flex flex-column flex-md-row justify-content-between align-items-center py-3 border-bottom">
            <div class="pb-2 pb-md-0" i18n>Menge im Warenkorb</div>
            <div class="d-flex justify-content-center">
              <button type="button" class="btn btn-sm btn-light-info rounded-circle"
                      (click)="decreaseQuantity()">
                <span class="material-icons fs-4">remove_circle_outline</span>
              </button>
              <input type="number" ngbAutofocus class="form-control mx-3 w-144px" [formControl]="quantityControl">
              <button type="button" class="btn btn-sm btn-light-info rounded-circle"
                      (click)="increaseQuantity()">
                <span class="material-icons fs-4">add_circle_outline</span>
              </button>
            </div>
          </div>
          <div class="d-flex flex-column flex-md-row justify-content-between align-items-center py-3">
            <div class="pb-2 pb-md-0" i18n>Rabatt hinzufügen</div>
            <div class="d-flex justify-content-center">
              <input type="number" class="form-control me-2" [formControl]="discountAmountControl">

              <div class="btn-group">
                <input type="radio" class="btn-check" id="discount-absolute"
                       [formControl]="discountTypeControl" autocomplete="off"
                       value="absolute">
                <label class="btn btn-outline-info fw-bold" for="discount-absolute">€</label>

                <input type="radio" class="btn-check" id="discount-relative"
                       [formControl]="discountTypeControl" autocomplete="off" value="relative">
                <label class="btn btn-outline-info fw-bold" for="discount-relative">%</label>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="modal-footer justify-content-center align-items-center justify-content-md-between flex-column-reverse flex-md-row p-3 p-md-4">
        <button type="button" class="btn btn-lg btn-danger w-100 w-md-auto mb-3 mb-md-0"
                (click)="removeProduct()" i18n [disabled]="loading" [appLoadingOverlay]="loading">Löschen
        </button>
        <button type="submit" class="btn btn-lg btn-info w-100 w-md-auto" i18n [disabled]="loading"
                [appLoadingOverlay]="loading">Änderungen
          speichern
        </button>
      </div>
    </form>`,
    styles: [`
    :host {
      display: block;
    }

    .product-info-button {
      transform: translate(50%, 5%);
      position: absolute;
      bottom: 0;
      left: 50%;
    }
  `],
    standalone: false
})
export class CartItemEditComponent implements OnInit {
  @Input() lineItem!: CartItem;

  loading = false;

  quantityControl = new UntypedFormControl(0, [Validators.min(1)]);
  discountTypeControl = new UntypedFormControl('absolute', []);
  discountAmountControl = new UntypedFormControl(0, []);

  form = new UntypedFormGroup({
    quantity: this.quantityControl,
    discountType: this.discountTypeControl,
    discountAmount: this.discountAmountControl
  });

  image = '';

  constructor(public activeModal: NgbActiveModal,
    private readonly productDetailsService: ProductDetailsService,
    private readonly dataService: DataService,
    private readonly router: Router) {
  }

  ngOnInit() {
    this.image = this.lineItem.image?.url || '';
    this.quantityControl.setValue(this.lineItem.quantity);
    this.discountTypeControl.setValue(this.lineItem.discountType);
    this.discountAmountControl.setValue(this.lineItem.discountAmount);
  }

  async updateProduct() {
    if (!this.form.valid) {
      return;
    }

    this.loading = true;
    try {
      await this.dataService.updateCartProduct({
        id: this.lineItem.id,
        ...this.form.value
      });
      this.activeModal.close();
    } finally {
      this.loading = false;
    }
  }

  async removeProduct() {
    this.loading = true;
    try {
      await this.dataService.removeFromCart(this.lineItem.id);

      if (!this.dataService.cart.lineItems.length) {
        this.router.navigate(['/']);
      }

      this.activeModal.close();
    } finally {
      this.loading = false;
    }
  }

  increaseQuantity() {
    const value = this.quantityControl.value;
    this.quantityControl.setValue(value + 1);
  }

  decreaseQuantity() {
    const value = this.quantityControl.value;
    if (value === 1) {
      return;
    }
    this.quantityControl.setValue(value - 1);
  }

  async onProductInfoClicked() {
    await this.productDetailsService.openProductInfoModal(this.lineItem.productId);
  }
}
